import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useActionState } from "react";
import {
  ArrivingFailed,
  ArrivingLayout,
  ArrivingPending,
} from "@/client/components/arriving-layout";
import { text } from "@/client/lib/form";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { sessionQuery } from "@/client/lib/session";
import { CreateCompanyForm } from "@/features/company/components/create-company-form";
import type { HeldProfile } from "@/features/company/use-create-company";
import { BackToCompany } from "@/features/identity/components/back-to-company";
import { ProfileStep } from "@/features/onboarding/components/profile-step";
import { profileStepInput } from "@/features/onboarding/schemas";
import { chosenPhoto } from "@/features/onboarding/use-onboarding-step";

export const Route = createFileRoute("/_authenticated/companies/new")({
  pendingMs: PENDING_MS,
  pendingComponent: ArrivingPending,
  errorComponent: ArrivingFailed,
  head: async () => {
    const { companyCopy } = await import("@/features/company/copy");
    return { meta: [{ title: pageTitle(companyCopy.create.title) }] };
  },
  component: CreateCompany,
});

interface Held {
  readonly profile: HeldProfile | null;
  readonly attempted: string;
  readonly problem: string | null;
}

/**
 * Step 1 asks for the name and photo, which are kept here until step 2 has made a company to keep
 * them in. A login that already has a name, from Google or another company, starts at step 2.
 */
function CreateCompany() {
  // The parent guard has already read the session, so this does not suspend.
  const { data } = useSuspenseQuery(sessionQuery);
  const named = data.name?.trim() ? { name: data.name, photo: null } : null;

  const [held, hold] = useActionState(
    (_: Held, form: FormData): Held => {
      const attempted = text(form, "name");
      const typed = profileStepInput.safeParse({ name: attempted });
      if (!typed.success) {
        return {
          profile: null,
          attempted,
          problem: typed.error.issues[0]?.message ?? null,
        };
      }
      return {
        profile: { name: typed.data.name, photo: chosenPhoto(form) },
        attempted,
        problem: null,
      };
    },
    { profile: named, attempted: "", problem: null },
  );

  return (
    <ArrivingLayout>
      {held.profile ? (
        <CreateCompanyForm
          profile={held.profile}
          removedFrom={data.removedFrom}
        />
      ) : (
        <ProfileStep
          position={1}
          total={3}
          defaultName={held.attempted}
          act={hold}
          busy={false}
          problem={held.problem}
          nameRefused={held.problem !== null}
        />
      )}
      {data.companySlug ? <BackToCompany /> : null}
    </ArrivingLayout>
  );
}
